import React from 'react';

interface Props {
  content: string;
}

export function MarkdownRenderer({ content }: Props) {
  const renderLine = (line: string, index: number): React.ReactNode => {
    // Título H2 (##)
    if (line.startsWith('## ')) {
      return <h2 key={index} style={{ fontSize: 15, fontWeight: 700, color: '#1f2937', margin: '14px 0 6px', borderBottom: '1px solid #e5e7eb', paddingBottom: 4 }}>{renderInline(line.slice(3))}</h2>;
    }
    // Título H3 (###)
    if (line.startsWith('### ')) {
      return <h3 key={index} style={{ fontSize: 14, fontWeight: 700, color: '#374151', margin: '10px 0 4px' }}>{renderInline(line.slice(4))}</h3>;
    }
    // Título H4 (####)
    if (line.startsWith('#### ')) {
      return <h4 key={index} style={{ fontSize: 13, fontWeight: 600, color: '#374151', margin: '8px 0 2px' }}>{renderInline(line.slice(5))}</h4>;
    }
    // Item de lista (- ou *)
    if (line.match(/^[-*] /)) {
      return (
        <div key={index} style={{ display: 'flex', gap: 8, margin: '3px 0', paddingLeft: 4 }}>
          <span style={{ color: '#059669', fontWeight: 700, flexShrink: 0 }}>•</span>
          <span>{renderInline(line.slice(2))}</span>
        </div>
      );
    }
    // Item numerado (1. 2. etc)
    if (line.match(/^\d+\. /)) {
      const num = line.match(/^(\d+)\. /)?.[1];
      return (
        <div key={index} style={{ display: 'flex', gap: 8, margin: '3px 0', paddingLeft: 4 }}>
          <span style={{ color: '#059669', fontWeight: 700, flexShrink: 0, minWidth: 16 }}>{num}.</span>
          <span>{renderInline(line.replace(/^\d+\. /, ''))}</span>
        </div>
      );
    }
    // Separador ---
    if (line.trim() === '---') {
      return <hr key={index} style={{ border: 'none', borderTop: '1px solid #e5e7eb', margin: '10px 0' }} />;
    }
    // Linha em branco
    if (line.trim() === '') {
      return <div key={index} style={{ height: 6 }} />;
    }
    // Parágrafo normal
    return <p key={index} style={{ margin: '3px 0', lineHeight: 1.65 }}>{renderInline(line)}</p>;
  };

  const renderInline = (text: string): React.ReactNode => {
    // Processar **negrito**, *itálico*, `código`, [link](url)
    const parts: React.ReactNode[] = [];
    let remaining = text;
    let keyIdx = 0;

    while (remaining.length > 0) {
      // **negrito**
      const boldMatch = remaining.match(/\*\*(.+?)\*\*/);
      // *itálico*
      const italicMatch = remaining.match(/(?<!\*)\*(?!\*)(.+?)(?<!\*)\*(?!\*)/);
      // `código`
      const codeMatch = remaining.match(/`(.+?)`/);
      // [texto](url)
      const linkMatch = remaining.match(/\[(.+?)\]\((.+?)\)/);

      // Encontrar qual vem primeiro
      const matches = [
        boldMatch ? { index: boldMatch.index!, type: 'bold', match: boldMatch } : null,
        italicMatch ? { index: italicMatch.index!, type: 'italic', match: italicMatch } : null,
        codeMatch ? { index: codeMatch.index!, type: 'code', match: codeMatch } : null,
        linkMatch ? { index: linkMatch.index!, type: 'link', match: linkMatch } : null,
      ].filter(Boolean).sort((a, b) => a!.index - b!.index);

      if (matches.length === 0) {
        parts.push(<span key={keyIdx++}>{remaining}</span>);
        break;
      }

      const first = matches[0]!;
      // Texto antes do match
      if (first.index > 0) {
        parts.push(<span key={keyIdx++}>{remaining.slice(0, first.index)}</span>);
      }

      // Renderizar o match
      if (first.type === 'bold') {
        parts.push(<strong key={keyIdx++} style={{ fontWeight: 700 }}>{first.match[1]}</strong>);
        remaining = remaining.slice(first.index + first.match[0].length);
      } else if (first.type === 'italic') {
        parts.push(<em key={keyIdx++}>{first.match[1]}</em>);
        remaining = remaining.slice(first.index + first.match[0].length);
      } else if (first.type === 'code') {
        parts.push(<code key={keyIdx++} style={{ background: '#f3f4f6', border: '1px solid #e5e7eb', borderRadius: 4, padding: '1px 5px', fontSize: 12, fontFamily: 'monospace', color: '#059669' }}>{first.match[1]}</code>);
        remaining = remaining.slice(first.index + first.match[0].length);
      } else if (first.type === 'link') {
        parts.push(<a key={keyIdx++} href={first.match[2]} target="_blank" rel="noopener noreferrer" style={{ color: '#059669', textDecoration: 'underline' }}>{first.match[1]}</a>);
        remaining = remaining.slice(first.index + first.match[0].length);
      }
    }

    return parts;
  };

  const lines = content.split('\n');

  return (
    <div style={{ fontSize: 14, lineHeight: 1.6, color: '#1f2937' }}>
      {lines.map((line, i) => renderLine(line, i))}
    </div>
  );
}